import React from "react";
import styled from "styled-components";
import { colorText, colorTextAdditional } from "../../../utils/palette";

const TRACK_COLOR = "#F1F3F5";

// Rank 1 (worst performer) gets widest bar.
// For higherIsBetter metrics, worst = lowest value → bar width inversely proportional.
// For !higherIsBetter metrics, worst = highest value → bar width directly proportional.
function barWidth(item, worstValue, higherIsBetter) {
  if (higherIsBetter) {
    // worstValue is the minimum (lowest mpg = worst)
    return Math.max(15, (worstValue / item.value) * 85 + 15);
  }
  // worstValue is the maximum (highest cost = worst)
  return Math.max(15, (item.value / worstValue) * 85 + 15);
}

// ── Styled components ──────────────────────────────────────────────────────────

const List = styled.div`
  display: grid;
  grid-template-columns: 22px fit-content(140px) 1fr auto;
  align-items: center;
  column-gap: 10px;
  row-gap: 8px;
`;

const Row = styled.div`
  display: contents;
`;

const Rank = styled.span`
  font-size: 11px;
  font-weight: 700;
  color: ${colorTextAdditional};
  text-align: right;
`;

const Name = styled.p`
  font-size: 12px;
  color: ${colorText};
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const BarTrack = styled.div`
  height: 16px;
  border-radius: 3px;
  background: ${TRACK_COLOR};
  overflow: hidden;
`;

const BarFill = styled.div`
  height: 100%;
  width: ${({ $pct }) => $pct}%;
  background: ${({ $color }) => $color};
  border-radius: 3px;
  transition: width 0.4s ease;
`;

const Value = styled.span`
  font-size: 12px;
  font-weight: 600;
  color: ${colorText};
  white-space: nowrap;
  min-width: 60px;
  text-align: right;
`;

// ── Component ─────────────────────────────────────────────────────────────────

export default function RankedBarChart({ data, formatFn, colour = '#495057' }) {
  const { data: items } = data;

  if (!items || items.length === 0) return null;

  const higherIsBetter = data.higherIsBetter ?? false;

  const worstValue = higherIsBetter
    ? Math.min(...items.map((i) => i.value)) // lowest mpg = worst
    : Math.max(...items.map((i) => i.value)); // highest cost = worst

  return (
    <List>
      {items.map((item, index) => {
        const pct = barWidth(item, worstValue, higherIsBetter);
        return (
          <Row key={item.id}>
            <Rank>{index + 1}</Rank>
            <Name title={item.label}>{item.label}</Name>
            <BarTrack>
              <BarFill $pct={pct} $color={colour} />
            </BarTrack>
            <Value>{formatFn ? formatFn(item.value) : item.value}</Value>
          </Row>
        );
      })}
    </List>
  );
}
